// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminFirstN.js
// Admin "First N Minutes" (replaces "QQQ First 5"): does the base's first N minutes of a New York
// session (pre / open / post) set the direction for the rest of it and the sessions after?
// Up holds the bull ETF, down the bear ETF. Runs are background jobs (POST /api/admin/firstn/run,
// then poll /api/admin/firstn/result/<job>; backend/firstn.py -> engines/firstn_backtest.py).
// Sections: settings, strategy results, signal stats per checked session, then the days
// (tap a day for its checks, trades and signal legs). Settings are kept in this browser.
import React, { useState, useMemo } from 'react';
import Navbar from '../components/Navbar';
import api from '../api';

const SESS = [['pre', 'Pre'], ['open', 'Open'], ['post', 'Post']];
const STORE = 'firstn_cfg';
const PRESETS = [['SOX', 'SOXX', 'SOXL', 'SOXS'], ['QQQ', 'QQQ', 'TQQQ', 'SQQQ']];
const today = () => new Date().toISOString().slice(0, 10);
const DEFAULTS = {
  base: 'SOXX', bull: 'SOXL', bear: 'SOXS', from: '2016-01-01', to: today(),
  n: { pre: 5, open: 5, post: 5 }, check: { pre: true, open: false, post: false },
  wait: 0, flat_pct: 0, hold: 'day', weekends: false, flat_rule: 'hold', start: 10000,
};

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '24px 16px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '6px' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '16px', marginBottom: '16px' },
  title:   { fontSize: '15px', fontWeight: 600, marginBottom: '10px' },
  sub:     { fontSize: '12px', fontWeight: 600, margin: '14px 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" },
  row:     { display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '10px' },
  label:   { display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" },
  check:   { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: 'var(--text)', cursor: 'pointer' },
  input:   { padding: '6px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--bg)', color: 'var(--text)', width: '90px' },
  btn:     { padding: '8px 18px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600, background: 'var(--accent)', color: '#fff' },
  btnG:    { padding: '4px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '12px', background: 'var(--bg)', color: 'var(--text)' },
  btnOn:   { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' },
  muted:   { color: 'var(--text-muted)', fontSize: '12px' },
  err:     { color: 'var(--error)', fontSize: '13px', margin: '8px 0' },
  tiles:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px' },
  tile:    { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px' },
  tl:      { fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono', monospace" },
  tv:      { fontSize: '17px', fontWeight: 600, fontFamily: "'DM Mono', monospace" },
  scroll:  { overflowX: 'auto' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: "'DM Mono', monospace" },
  th:      { textAlign: 'right', color: 'var(--text-muted)', fontWeight: 500, padding: '4px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td:      { textAlign: 'right', padding: '4px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  detail:  { padding: '8px 4px 12px', fontSize: '12px', fontFamily: "'DM Mono', monospace", background: 'var(--bg)', borderBottom: '1px solid var(--border)', whiteSpace: 'normal', textAlign: 'left' },
};

const col = v => v > 0 ? 'var(--success)' : v < 0 ? 'var(--error)' : 'var(--text-muted)';
const pct = (v, d = 2) => v == null ? '—' : (v > 0 ? '+' : '') + Number(v).toFixed(d) + '%';
const rate = v => v == null ? '—' : Number(v).toFixed(1) + '%';
const money = v => v == null ? '—' : '$' + Number(v).toLocaleString('en-AU', { maximumFractionDigits: 0 });
const arrow = d => d === 'up' ? '▲' : d === 'down' ? '▼' : '–';
const sleep = ms => new Promise(r => setTimeout(r, ms));
const store = {
  get: () => { try { return JSON.parse(localStorage.getItem(STORE)) || {}; } catch { return {}; } },
  set: v => { try { localStorage.setItem(STORE, JSON.stringify(v)); } catch { /* storage unavailable */ } },
};
const loadCfg = () => {
  const v = store.get();
  return { ...DEFAULTS, ...v, to: v.to || today(), n: { ...DEFAULTS.n, ...(v.n || {}) }, check: { ...DEFAULTS.check, ...(v.check || {}) } };
};

// Wait for a background run. Network errors / a sleeping phone don't end the wait.
async function waitFor(job) {
  const until = Date.now() + 30 * 60 * 1000;
  while (Date.now() < until) {
    await sleep(2500);
    try {
      const res = await api.get(`/admin/firstn/result/${job}`);
      if (res.status === 200) return res.data;
    } catch (e) {
      const st = e.response?.status;
      if (st === 404 || st === 400) return { error: e.response?.data?.error || 'Run not found' };
    }
  }
  return { error: 'Still running after 30 minutes - press Run again later' };
}

function Field({ label, children }) {
  return <label style={s.label}>{label}{children}</label>;
}

function Tile({ label, value, color, sub }) {
  return (
    <div style={s.tile}>
      <div style={s.tl}>{label}</div>
      <div style={{ ...s.tv, color: color || 'var(--text)' }}>{value}</div>
      {sub && <div style={s.muted}>{sub}</div>}
    </div>
  );
}

// avg / median / win / best / worst for a list of % returns (engine _summ).
function SummCells({ v }) {
  if (!v || !v.n) return <><td style={s.td}>0</td><td style={s.td} colSpan={5}>—</td></>;
  return (
    <>
      <td style={s.td}>{v.n.toLocaleString()}</td>
      <td style={s.td}>{rate(v.win)}</td>
      <td style={{ ...s.td, color: col(v.avg) }}>{pct(v.avg)}</td>
      <td style={{ ...s.td, color: col(v.median) }}>{pct(v.median)}</td>
      <td style={{ ...s.td, color: col(v.best) }}>{pct(v.best)}</td>
      <td style={{ ...s.td, color: col(v.worst) }}>{pct(v.worst)}</td>
    </>
  );
}

function Strategy({ st, cfg }) {
  return (
    <div style={s.section}>
      <div style={s.title}>Strategy results <span style={s.muted}>({cfg.bull} on up, {cfg.bear} on down, all in, no costs)</span></div>
      <div style={s.tiles}>
        <Tile label="Final" value={money(st.final)} sub={`from ${money(st.start)}`} />
        <Tile label="Total" value={pct(st.total, 1)} color={col(st.total)} />
        <Tile label="Max drawdown" value={pct(st.max_dd, 1)} color="var(--error)" />
        <Tile label="Day win rate" value={rate(st.active_days.win)} sub={`${st.active_days.n || 0} days invested`} />
        <Tile label="Avg day" value={pct(st.active_days.avg)} color={col(st.active_days.avg)} sub={`median ${pct(st.active_days.median)}`} />
        <Tile label="Trades" value={(st.trades.n || 0).toLocaleString()} sub={st.trades.n ? `${rate(st.trades.win)} win, avg ${pct(st.trades.avg)}` : ''} />
      </div>
      <div style={s.sub}>Per session (days with a move)</div>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr><th style={{ ...s.th, textAlign: 'left' }}>Session</th><th style={s.th}>Days</th><th style={s.th}>Win</th><th style={s.th}>Avg</th><th style={s.th}>Median</th><th style={s.th}>Best</th><th style={s.th}>Worst</th></tr></thead>
          <tbody>
            {[...SESS, ['day', 'Whole day']].map(([k, l]) => (
              <tr key={k}><td style={{ ...s.td, textAlign: 'left' }}>{l}</td><SummCells v={k === 'day' ? st.active_days : st.sessions[k]} /></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={s.sub}>By year</div>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr><th style={{ ...s.th, textAlign: 'left' }}>Year</th><th style={s.th}>Total</th><th style={s.th}>Days</th><th style={s.th}>Win</th><th style={s.th}>Avg day</th></tr></thead>
          <tbody>
            {Object.entries(st.years).reverse().map(([y, v]) => (
              <tr key={y}>
                <td style={{ ...s.td, textAlign: 'left' }}>{y}</td>
                <td style={{ ...s.td, color: col(v.total) }}>{pct(v.total, 1)}</td>
                <td style={s.td}>{v.days}</td><td style={s.td}>{rate(v.win)}</td>
                <td style={{ ...s.td, color: col(v.avg) }}>{pct(v.avg)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SignalCard({ k, v, cfg }) {
  const [grp, setGrp] = useState('all');
  const g = v[grp];
  const label = SESS.find(x => x[0] === k)[1];
  return (
    <div style={s.section}>
      <div style={{ ...s.title, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <span>{label} first {cfg.n[k]} min {cfg.wait ? `(+${cfg.wait} wait)` : ''} → rest of the day</span>
        <span style={{ display: 'flex', gap: '4px' }}>
          {[['all', 'All'], ['up', `Up (${cfg.bull})`], ['down', `Down (${cfg.bear})`]].map(([id, l]) => (
            <button key={id} style={{ ...s.btnG, ...(grp === id ? s.btnOn : {}) }} onClick={() => setGrp(id)}>{l}</button>
          ))}
        </span>
      </div>
      <div style={s.tiles}>
        <Tile label="Signals" value={g.n.toLocaleString()} sub={v.skipped ? `${v.skipped} with no ETF trade` : ''} />
        <Tile label="Kept to post close" value={rate(g.all_hit)} sub="every session after the window" />
      </div>
      <div style={s.sub}>Held from minute {cfg.n[k] + 1} to the end of…</div>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr>
            <th style={{ ...s.th, textAlign: 'left' }}>Session end</th><th style={s.th}>Base kept dir</th>
            <th style={s.th}>ETF avg</th><th style={s.th}>Median</th><th style={s.th}>Win</th><th style={s.th}>Best</th><th style={s.th}>Worst</th><th style={s.th}>That session avg</th>
          </tr></thead>
          <tbody>
            {Object.entries(g.legs).map(([leg, l]) => (
              <tr key={leg}>
                <td style={{ ...s.td, textAlign: 'left' }}>{SESS.find(x => x[0] === leg)[1]}</td>
                <td style={s.td}>{rate(l.hit)}</td>
                <td style={{ ...s.td, color: col(l.cum.avg) }}>{pct(l.cum.avg)}</td>
                <td style={{ ...s.td, color: col(l.cum.median) }}>{pct(l.cum.median)}</td>
                <td style={s.td}>{rate(l.cum.win)}</td>
                <td style={{ ...s.td, color: col(l.cum.best) }}>{pct(l.cum.best)}</td>
                <td style={{ ...s.td, color: col(l.cum.worst) }}>{pct(l.cum.worst)}</td>
                <td style={{ ...s.td, color: col(l.seg_avg) }}>{pct(l.seg_avg)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ ...s.muted, marginTop: '6px' }}>
        "Base kept dir" = the base finished that session beyond the window's last price in the signal's direction.
        ETF columns are the bull/bear return from the entry to that session's last minute; "That session avg" is only that session's part.
      </div>
      <div style={s.sub}>By year (to post close, all signals)</div>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr><th style={{ ...s.th, textAlign: 'left' }}>Year</th><th style={s.th}>Signals</th><th style={s.th}>Kept dir</th><th style={s.th}>ETF avg</th></tr></thead>
          <tbody>
            {Object.entries(v.years).reverse().map(([y, r]) => (
              <tr key={y}><td style={{ ...s.td, textAlign: 'left' }}>{y}</td><td style={s.td}>{r.n}</td><td style={s.td}>{rate(r.hit)}</td><td style={{ ...s.td, color: col(r.avg) }}>{pct(r.avg)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Days({ data }) {
  const years = useMemo(() => [...new Set(data.days.map(d => d.d.slice(0, 4)))].reverse(), [data]);
  const [year, setYear] = useState(years[0]);
  const [open, setOpen] = useState(null);
  const days = data.days.filter(d => d.d.startsWith(year)).reverse();
  return (
    <div style={s.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ ...s.title, marginBottom: 0 }}>Days</div>
        <select style={{ ...s.input, width: 'auto' }} value={year} onChange={e => { setYear(e.target.value); setOpen(null); }}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr>
            <th style={{ ...s.th, textAlign: 'left' }}>Date</th>
            {SESS.map(([k, l]) => <th key={k} style={s.th}>{l}</th>)}
            <th style={s.th}>Day</th><th style={s.th}>Equity</th><th style={s.th}>Holding</th>
          </tr></thead>
          <tbody>
            {days.map(d => (
              <React.Fragment key={d.d}>
                <tr style={{ cursor: 'pointer' }} onClick={() => setOpen(open === d.d ? null : d.d)}>
                  <td style={{ ...s.td, textAlign: 'left' }}>{d.d.slice(5)}</td>
                  {SESS.map(([k]) => {
                    const x = d.s[k] || {};
                    return (
                      <td key={k} style={s.td}>
                        {x.c && <span style={{ color: col(x.c.dir === 'up' ? 1 : x.c.dir === 'down' ? -1 : 0) }}>{arrow(x.c.dir)} </span>}
                        <span style={{ color: col(x.ret) }}>{x.ret ? pct(x.ret) : '—'}</span>
                      </td>
                    );
                  })}
                  <td style={{ ...s.td, color: col(d.ret) }}>{d.ret ? pct(d.ret) : '—'}</td>
                  <td style={s.td}>{money(d.eq)}</td>
                  <td style={s.td}>{d.pos || ''}</td>
                </tr>
                {open === d.d && (
                  <tr><td colSpan={7} style={s.detail}>
                    {SESS.map(([k, l]) => {
                      const x = d.s[k] || {};
                      const sig = d.sig[k];
                      return (
                        <div key={k} style={{ marginBottom: '8px' }}>
                          <b>{l}</b>{' '}
                          {x.c ? <>check {arrow(x.c.dir)} {x.c.mv == null ? 'no base trade' : pct(x.c.mv, 3)}{x.c.w ? ' (after wait)' : ''}{x.c.why ? ` - ${x.c.why}` : ''}. </> : 'not checked. '}
                          {(x.acts || []).join(' · ')}
                          {sig && sig.legs && (
                            <div style={s.muted}>
                              signal: {sig.dir === 'up' ? data.cfg.bull : data.cfg.bear} in {sig.in_t} @ {sig.in} →{' '}
                              {sig.legs.map(lg => `${lg.s} ${lg.hit == null ? '?' : lg.hit ? '✓' : '✗'} ${pct(lg.cum)}`).join(', ')}
                            </div>
                          )}
                          {sig && sig.skip && <div style={s.muted}>signal: {sig.skip}</div>}
                        </div>
                      );
                    })}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ ...s.muted, marginTop: '6px' }}>▲/▼ = that session's check. Session % includes any overnight move into it. Tap a day for details.</div>
    </div>
  );
}

export default function AdminFirstN() {
  const [cfg, setCfg] = useState(loadCfg);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);

  const set = (k, v) => setCfg(c => { const n = { ...c, [k]: v }; store.set(n); return n; });
  const setIn = (k, sub, v) => setCfg(c => { const n = { ...c, [k]: { ...c[k], [sub]: v } }; store.set(n); return n; });
  const num = v => v === '' ? '' : Number(v);

  const run = async () => {
    setRunning(true); setError('');
    try {
      const job = (await api.post('/admin/firstn/run', cfg)).data.job;
      const res = await waitFor(job);
      if (res.error) setError(res.error); else setData(res);
    } catch (e) {
      setError(e.response?.data?.error || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={s.page}>
      <Navbar backTo={{ label: '← Admin', href: '/admin' }} />
      <div style={s.content}>
        <div style={s.heading}>First N Minutes</div>
        <div style={{ ...s.muted, marginBottom: '16px' }}>
          Does the base's first N minutes of a New York session (pre 04:00, open 09:30, post at the close) set the direction for the rest of it and the sessions after?
          Up buys the bull ETF, down the bear ETF, at the next traded minute's open; exits at the open of the session's last traded minute. All in, compounding, no costs. Recorded minute bars only.
        </div>

        <div style={s.section}>
          <div style={s.title}>Settings</div>
          <div style={s.row}>
            {PRESETS.map(([l, b, u, d]) => (
              <button key={l} style={s.btnG} onClick={() => setCfg(c => { const n = { ...c, base: b, bull: u, bear: d }; store.set(n); return n; })}>{l} trio</button>
            ))}
          </div>
          <div style={s.row}>
            {[['base', 'Base'], ['bull', 'Bull (up)'], ['bear', 'Bear (down)']].map(([k, l]) => (
              <Field key={k} label={l}><input style={s.input} value={cfg[k]} onChange={e => set(k, e.target.value.toUpperCase())} /></Field>
            ))}
            <Field label="From"><input style={{ ...s.input, width: '140px' }} type="date" value={cfg.from} onChange={e => set('from', e.target.value)} /></Field>
            <Field label="To"><input style={{ ...s.input, width: '140px' }} type="date" value={cfg.to} onChange={e => set('to', e.target.value)} /></Field>
          </div>
          <div style={s.row}>
            {SESS.map(([k, l]) => (
              <div key={k} style={{ ...s.tile, display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                <label style={s.check}><input type="checkbox" checked={cfg.check[k]} onChange={e => setIn('check', k, e.target.checked)} />Check {l}</label>
                <Field label="N min"><input style={{ ...s.input, width: '60px' }} type="number" min="1" value={cfg.n[k]} onChange={e => setIn('n', k, num(e.target.value))} /></Field>
              </div>
            ))}
          </div>
          <div style={s.row}>
            <Field label="If flat, wait X more min"><input style={s.input} type="number" min="0" value={cfg.wait} onChange={e => set('wait', num(e.target.value))} /></Field>
            <Field label="Flat under Y %"><input style={s.input} type="number" min="0" step="0.01" value={cfg.flat_pct} onChange={e => set('flat_pct', num(e.target.value))} /></Field>
            <Field label="Hold">
              <select style={{ ...s.input, width: 'auto' }} value={cfg.hold} onChange={e => set('hold', e.target.value)}>
                <option value="session">Close each session</option>
                <option value="day">Close at end of day</option>
                <option value="all">Hold across sessions</option>
              </select>
            </Field>
            {cfg.hold === 'all' && (
              <label style={{ ...s.check, paddingBottom: '6px' }}><input type="checkbox" checked={cfg.weekends} onChange={e => set('weekends', e.target.checked)} />Also over weekends / holidays</label>
            )}
            <Field label="When flat">
              <select style={{ ...s.input, width: 'auto' }} value={cfg.flat_rule} onChange={e => set('flat_rule', e.target.value)}>
                <option value="hold">Keep the position</option>
                <option value="cash_session">Cash for the session</option>
                <option value="cash_day">Cash for the day</option>
              </select>
            </Field>
            <Field label="Starting $"><input style={{ ...s.input, width: '110px' }} type="number" min="1" value={cfg.start} onChange={e => set('start', num(e.target.value))} /></Field>
          </div>
          <button style={{ ...s.btn, opacity: running ? 0.6 : 1 }} disabled={running} onClick={run}>{running ? 'Running…' : 'Run'}</button>
          {running && <span style={{ ...s.muted, marginLeft: '10px' }}>Long ranges take a minute or two.</span>}
          {error && <div style={s.err}>{error}</div>}
        </div>

        {data && (
          <>
            <div style={{ ...s.muted, marginBottom: '10px' }}>
              {data.cfg.base} → {data.cfg.bull} / {data.cfg.bear}, {data.range?.[0]} to {data.range?.[1]} ({data.strategy.days.toLocaleString()} trading days).
              Data: {['base', 'bull', 'bear'].map(r => `${data.cfg[r]} ${data.coverage[r] ? data.coverage[r].join(' to ') : 'none'}`).join(' · ')}.
            </div>
            <Strategy st={data.strategy} cfg={data.cfg} />
            {Object.entries(data.signals).map(([k, v]) => <SignalCard key={k} k={k} v={v} cfg={data.cfg} />)}
            <Days data={data} />
          </>
        )}
      </div>
    </div>
  );
}
